import React from "react";
import { format } from "date-fns";
import { alpha, Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { ChartTooltip } from "@components/Tooltip/ChartTooltip";
import { toCurrency } from "@helpers";
import type { MandatorType } from "@rentablo/theming";

export type Point = {
  year: number;
  y: number;
  index: number;
  dividends: {
    amount: number;
    name: string;
    isPrediction: boolean;
    investmentId?: number;
  }[];
};

type Props = {
  point: Point;
  mode: "short" | "long";
  maxHeight?: string | number;
  theme?: Theme;
  mandatorSlug?: MandatorType;
  receivedColor?: string;
  predictedColor?: string;
  currency: string;
};

const getSecondaryTextColor = (
  theme: Theme | undefined,
  mandatorSlug?: MandatorType
) =>
  mandatorSlug === "ftd"
    ? theme?.palette.text.secondary
    : theme?.palette.text.disabled;

const getOnTooltipPrimaryColor = (
  theme: Theme | undefined,
  mandatorSlug?: MandatorType
) =>
  mandatorSlug === "ftd"
    ? theme?.palette.text.onTooltipPrimary
    : theme?.palette.text.disabled;

const Circle = ({
  isPrediction,
  receivedColor,
  predictedColor,
}: {
  isPrediction: boolean;
  receivedColor?: string;
  predictedColor?: string;
}) => {
  return isPrediction ? (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      style={{
        flexShrink: 0,
        marginTop: "3px",
      }}
    >
      <circle
        cx="6"
        cy="6"
        r="5.25"
        stroke={predictedColor}
        style={{
          strokeDasharray: "2 2",
          strokeWidth: "1.5",
        }}
      />
    </svg>
  ) : (
    <div
      style={{
        width: "12px",
        height: "12px",
        boxSizing: "border-box",
        flexShrink: 0,
        marginTop: "3px",
        borderRadius: "50%",
        backgroundColor: receivedColor,
      }}
    />
  );
};

const Legend = ({
  theme,
  mandatorSlug,
  receivedColor,
  predictedColor,
}: {
  theme?: Theme;
  mandatorSlug?: MandatorType;
  receivedColor?: string;
  predictedColor?: string;
}) => {
  const secondaryColor = getSecondaryTextColor(theme, mandatorSlug);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "20px",
        marginBottom: "4px",
        paddingTop: "32px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <Circle
          isPrediction={false}
          receivedColor={receivedColor}
          predictedColor={predictedColor}
        />
        <span
          style={{
            ...theme?.typography?.caption,
            color: secondaryColor,
          }}
        >
          {i18next.t("dividendPlannerPage.chart.tooltipReceived") as string}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <Circle
          isPrediction
          receivedColor={receivedColor}
          predictedColor={predictedColor}
        />
        <span
          style={{
            ...theme?.typography?.caption,
            color: secondaryColor,
          }}
        >
          {i18next.t("dividendPlannerPage.chart.tooltipPredicted") as string}
        </span>
      </div>
    </div>
  );
};

export const DividendsTooltip = ({
  point,
  mode,
  maxHeight,
  theme,
  mandatorSlug,
  receivedColor: receivedColorProp,
  predictedColor: predictedColorProp,
  currency,
}: Props) => {
  const { t } = useTranslation();

  if (!point.dividends?.length) return <></>;

  const tooltipPrimary =
    theme?.palette.background.tooltipPrimary ??
    theme?.palette.background.paper ??
    "transparent";

  const secondaryColor = getSecondaryTextColor(theme, mandatorSlug);
  const onTooltipPrimaryColor = getOnTooltipPrimaryColor(theme, mandatorSlug);
  const receivedColor =
    receivedColorProp ?? theme?.palette.graphVisual["01"].dark;
  const predictedColor =
    predictedColorProp ?? theme?.palette.graphVisual["04"].mid;

  return (
    <ChartTooltip theme={theme} noPadding interactive>
      <div
        style={{
          width: "230px",
          maxHeight: "300px",
          whiteSpace: "normal",
          padding: "8px",
        }}
      >
        <div
          style={{
            ...theme?.typography?.caption,
            color: secondaryColor,
            marginBottom: "8px",
          }}
        >
          {format(
            new Date(point.year, point.index),
            mode === "short" ? "yyyy" : "MMMM yyyy"
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            maxHeight: maxHeight ?? "200px",
            overflow: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
          }}
        >
          {point.dividends.map((dividend, index) => (
            <div
              key={`${point.year}-${point.index}-${dividend.investmentId ?? "na"}-${index}`}
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingBottom:
                  index === point.dividends.length - 1 ? "100px" : "0",
                gap: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: theme?.spacing(1.5),
                }}
              >
                <Circle
                  isPrediction={dividend.isPrediction}
                  receivedColor={receivedColor}
                  predictedColor={predictedColor}
                />
                <div
                  style={{
                    fontSize: theme?.typography.caption.fontSize,
                    lineHeight: theme?.typography.caption.lineHeight,
                    color: onTooltipPrimaryColor,
                    fontWeight: "400",
                  }}
                >
                  {dividend.name}
                </div>
              </div>
              <div
                style={{
                  color: onTooltipPrimaryColor,
                  fontSize: theme?.typography.body3.fontSize,
                  lineHeight: "20px",
                  fontWeight: "600",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {toCurrency(dividend.amount, currency)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          bottom: "8px",
          left: "0",
          boxSizing: "border-box",
          padding: "0 16px 8px",
          margin: "0 -8px -8px",
          width: "calc(100% + 16px)",
          backgroundColor: "transparent",
          backgroundImage: `linear-gradient(to top, ${alpha(
            tooltipPrimary,
            1
          )} 80px, ${alpha(tooltipPrimary, 1)} 80px, transparent)`,
        }}
      >
        <Legend
          theme={theme}
          mandatorSlug={mandatorSlug}
          receivedColor={receivedColor}
          predictedColor={predictedColor}
        />
        <div
          style={{
            padding: "8px",
            border: `1px solid ${theme?.palette.border.onDark}`,
            color: theme?.palette.text.positive,
            fontSize: theme?.typography.caption.fontSize,
            fontWeight: "600",
            lineHeight: "16px",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            width: "100%",
            background: theme?.palette.background.tooltipPrimary,
          }}
        >
          <span>{toCurrency(point.y, currency)}</span>
          <span>
            {t("dividendPlannerPage.chart.total")}{" "}
            {format(
              new Date(point.year, point.index),
              mode === "short" ? "yyyy" : "MMMM yyyy"
            )}
          </span>
        </div>
      </div>
    </ChartTooltip>
  );
};
